import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import { EyeIcon, EyeOffIcon } from '../components/PasswordToggleIcon';

export default function ForcePasswordChangePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeLogin } = useAuth();

  const tempToken = location.state?.tempToken;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!tempToken) {
    navigate('/login', { replace: true });
    return null;
  }

  const rules = [
    { label: 'At least 12 characters',       met: newPassword.length >= 12 },
    { label: 'At least one uppercase letter', met: /[A-Z]/.test(newPassword) },
    { label: 'At least one lowercase letter', met: /[a-z]/.test(newPassword) },
    { label: 'At least one number',           met: /[0-9]/.test(newPassword) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/force-password-change', {
        token: tempToken,
        new_password: newPassword,
      });

      const data = response.data;

      if (data.requires_mfa_enrollment) {
        navigate('/enroll-mfa', {
          state: { enrollmentToken: data.enrollment_token },
          replace: true,
        });
        return;
      }

      const { access_token, refresh_token, user } = data;
      completeLogin(access_token, refresh_token, user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? (detail[0]?.msg || '').replace(/^Value error,\s*/i, '')
        : detail;
      setError(message || 'Password change failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Nephr&#333;s</h1>
          <p style={styles.subtitle}>Set Your Password</p>
        </div>

        <p style={styles.instructions}>
          Welcome to Nephros. Please set a new password before continuing.
        </p>

        {error && (
          <div style={styles.errorBanner}>{error}</div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="newPassword">New Password</label>

            {/* Password requirements — live feedback as user types */}
            <div style={styles.requirementsBlock}>
              {rules.map(({ label, met }) => (
                <div key={label} style={styles.requirementRow}>
                  <span style={met ? styles.checkMet : styles.checkUnmet}>
                    {met ? '✓' : '○'}
                  </span>
                  <span style={styles.requirementText}>{label}</span>
                </div>
              ))}
              <div style={styles.requirementRow}>
                <span style={styles.checkInfo}>◌</span>
                <span style={styles.requirementTextMuted}>
                  Must not be a previously breached password — checked on submission
                </span>
              </div>
            </div>

            <div style={styles.passwordWrapper}>
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ ...styles.input, ...styles.passwordInput }}
                autoComplete="new-password"
                autoFocus
                required
              />
              {/* Eye toggle is an inline icon control — plain button intentional */}
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={styles.showHideButton}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              autoComplete="new-password"
              required
            />
          </div>

          <Button type="submit" disabled={loading} style={{ width: '100%', marginTop: '4px' }}>
            {loading ? 'Saving...' : 'Set Password & Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-bg-page)', fontFamily: 'var(--font-family-base)' },
  card: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: '48px', width: '100%', maxWidth: '400px' },
  header: { textAlign: 'center', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-nav)', margin: '0 0 4px 0', letterSpacing: '-0.5px', fontFamily: 'var(--font-family-base)' },
  subtitle: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  instructions: { fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '24px', lineHeight: '1.5', fontFamily: 'var(--font-family-base)' },
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '20px', fontFamily: 'var(--font-family-base)' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  input: { padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', outline: 'none', fontFamily: 'var(--font-family-base)' },
  passwordWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  passwordInput: { flex: 1, paddingRight: '40px' },
  showHideButton: { position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0', display: 'flex', alignItems: 'center' },
  requirementsBlock: { backgroundColor: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '6px' },
  requirementRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  checkMet: { fontSize: 'var(--font-size-xs)', color: '#059669', fontWeight: 'var(--font-weight-bold)', width: '14px', flexShrink: 0 },
  checkUnmet: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-placeholder)', width: '14px', flexShrink: 0 },
  checkInfo: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-placeholder)', width: '14px', flexShrink: 0 },
  requirementText: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  requirementTextMuted: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-placeholder)', fontStyle: 'italic', fontFamily: 'var(--font-family-base)' },
};